<template>
  <label class="file-upload-ocr" :class="statusClass" @click="open">
    <span class="ocr-badge">
      <i class="uil uil-scenery"></i>
      OCR
    </span>

    <template v-if="hasPreview && !isProcessing">
      <i
        class="uil uil-times reset-btn"
        @click.prevent.stop="reset"
        title="Quitar archivo"
      ></i>
      <img v-if="previewType === 'image'" :src="imageUrl" />
      <div v-else-if="previewType === 'pdf'" class="pdf-preview">
        <i class="uil uil-file-pdf-alt pdf-preview__icon"></i>
        <span class="pdf-preview__name" :title="fileName">{{ fileName }}</span>
      </div>
      <span v-if="ocrStatus === 'done'" class="ocr-status ocr-status--ok">
        <i class="uil uil-check-circle"></i>
        Lectura OCR completada
      </span>
      <span v-else-if="ocrStatus === 'error'" class="ocr-status ocr-status--err">
        <i class="uil uil-exclamation-triangle"></i>
        {{ errorMessage || "Error al leer el documento" }}
      </span>
    </template>

    <template v-else-if="isProcessing">
      <i class="uil uil-file-search-alt processing-icon"></i>
      <span class="processing-label">{{ processingLabel }}</span>
      <div class="progress-track">
        <div class="progress-bar" :style="{ width: progress + '%' }"></div>
      </div>
      <span class="progress-text">{{ Math.round(progress) }}%</span>
    </template>

    <template v-else>
      <i :class="iconClass"></i>
      <span class="upload-label">{{ label }}</span>
      <span class="upload-hint">Lectura automática con OCR</span>
      <span class="upload-hint upload-hint--accept">{{ acceptedHint }}</span>
      <span
        v-if="ocrStatus === 'error' && errorMessage"
        class="ocr-status ocr-status--err"
      >
        <i class="uil uil-exclamation-triangle"></i>
        {{ errorMessage }}
      </span>
    </template>

    <input
      :accept="acceptAttr"
      type="file"
      ref="fileupload"
      :name="name"
      class="hidden"
      @change="setFile"
    />
  </label>
</template>

<script>
import { computed, onMounted, ref } from "vue";
const ocrEnabled = computed(() => process.env.VUE_APP_OCR_ENABLED || false);
const ocrApiUrl = computed(() => process.env.VUE_APP_OCR_API_URL || "");
const ocrApiKey = computed(() => process.env.VUE_APP_OCR_API_KEY || "");

export default {
  props: {
    icon: { type: String },
    label: { type: String },
    name: { type: String },
    accept: { type: String },
  },
  emits: ["ocr-success", "ocr-error", "ocr-progress"],
  setup(props, { emit }) {
    const fileupload = ref(null);
    const imageUrl = ref(null);
    const previewType = ref("none"); // none | image | pdf
    const fileName = ref("");
    const progress = ref(0);
    const ocrStatus = ref("idle"); // idle | processing | done | error
    const ocrData = ref(null);
    const errorMessage = ref("");

    const FILE_TYPE_CONFIG = {
      image: {
        accept: "image/png,image/jpg,image/jpeg",
        mimeTypes: ["image/png", "image/jpg", "image/jpeg"],
        extensions: ["png", "jpg", "jpeg"],
        maxSizeMB: 5,
        hint: "JPG, PNG o JPEG (máx. 5MB)",
      },
      pdf: {
        accept: "application/pdf",
        mimeTypes: ["application/pdf"],
        extensions: ["pdf"],
        maxSizeMB: 10,
        hint: "PDF (máx. 10MB)",
      },
    };

    const detectFileType = (file) => {
      const mime = (file?.type || "").toLowerCase();
      const ext = (file?.name?.split(".").pop() || "").toLowerCase();
      for (const [key, cfg] of Object.entries(FILE_TYPE_CONFIG)) {
        if (cfg.mimeTypes.includes(mime) || cfg.extensions.includes(ext)) {
          return key;
        }
      }
      return null;
    };

    const acceptAttr = computed(
      () =>
        props.accept ||
        Object.values(FILE_TYPE_CONFIG)
          .map((cfg) => cfg.accept)
          .join(",")
    );
    const acceptedHint = computed(() =>
      Object.values(FILE_TYPE_CONFIG)
        .map((cfg) => cfg.hint)
        .join(" / ")
    );

    const validateFile = (file) => {
      const detected = detectFileType(file);
      if (!detected) {
        const allExts = Object.values(FILE_TYPE_CONFIG)
          .flatMap((cfg) => cfg.extensions)
          .map((e) => e.toUpperCase())
          .join(", ");
        return {
          error: `Formato no permitido. Solo se aceptan: ${allExts}.`,
          detected: null,
        };
      }
      const { maxSizeMB } = FILE_TYPE_CONFIG[detected];
      const maxBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        return {
          error: `El archivo ${detected.toUpperCase()} excede el tamaño máximo permitido (${maxSizeMB}MB).`,
          detected,
        };
      }
      return { error: null, detected };
    };

    onMounted(() => {
      fileupload.value = null;
      imageUrl.value = null;
      previewType.value = "none";
      fileName.value = "";
      progress.value = 0;
      ocrStatus.value = "idle";
    });

    const fileToBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result || "";
          const base64 = String(result).split(",")[1] || "";
          resolve(base64);
        };
        reader.onerror = () => reject(reader.error || new Error("No se pudo leer el archivo"));
        reader.readAsDataURL(file);
      });

    const runOCR = async (file) => {
      progress.value = 0;
      emit("ocr-progress", 0);

      const interval = setInterval(() => {
        progress.value = Math.min(progress.value + 3, 90);
        emit("ocr-progress", progress.value);
      }, 150);

      try {
        const base64 = await fileToBase64(file);

        const baseUrl = (ocrApiUrl.value || "").replace(/\/+$/, "");
        if (!baseUrl) {
          throw new Error("VUE_APP_OCR_API_URL no está configurada");
        }
        const url = `${baseUrl}/glm-ocr/document/base64`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${ocrApiKey.value}`,
            "return_crop_images": true,
            "need_layout_visualization": true,
          },
          body: JSON.stringify({ document_base64: base64 }),
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => "");
          throw new Error(
            `OCR request failed (${response.status}): ${errorText || response.statusText}`
          );
        }

        const data = await response.json();
        progress.value = 100;
        emit("ocr-progress", 100);
        return data;
      } finally {
        clearInterval(interval);
      }
    };

    const setFile = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const { error: validationError, detected } = validateFile(file);
      if (validationError) {
        errorMessage.value = validationError;
        ocrStatus.value = "error";
        imageUrl.value = null;
        previewType.value = "none";
        fileName.value = "";
        if (fileupload.value) fileupload.value.value = "";
        emit("ocr-error", new Error(validationError));
        return;
      }

      errorMessage.value = "";
      fileName.value = file.name || "";

      if (detected === "image") {
        imageUrl.value = URL.createObjectURL(file);
        previewType.value = "image";
      } else {
        imageUrl.value = null;
        previewType.value = "pdf";
      }

      ocrStatus.value = "processing";
      progress.value = 0;

      try {
        const data = await runOCR(file);
        ocrData.value = data;
        ocrStatus.value = "done";
        emit("ocr-success", data);
      } catch (err) {
        ocrStatus.value = "error";
        errorMessage.value = err?.message || "Error al leer el documento";
        emit("ocr-error", err);
      }
    };

    const open = () => {
      if (ocrStatus.value === "processing") return;
      fileupload.value && fileupload.value.click();
    };

    const reset = () => {
      imageUrl.value = null;
      previewType.value = "none";
      fileName.value = "";
      progress.value = 0;
      ocrStatus.value = "idle";
      ocrData.value = null;
      errorMessage.value = "";
      if (fileupload.value) fileupload.value.value = "";
    };

    const iconClass = computed(() => `uil uil-${props.icon || "file-upload-alt"}`);
    const isProcessing = computed(() => ocrStatus.value === "processing");
    const hasPreview = computed(() => previewType.value !== "none");
    const statusClass = computed(() => ({
      "is-processing": ocrStatus.value === "processing",
      "is-done": ocrStatus.value === "done",
      "is-error": ocrStatus.value === "error",
      "has-file": hasPreview.value && ocrStatus.value !== "processing",
    }));
    const processingLabel = computed(() => {
      if (progress.value < 30) return "Cargando documento...";
      if (progress.value < 70) return "Analizando con OCR...";
      if (progress.value < 100) return "Extrayendo datos...";
      return "Listo";
    });

    return {
      iconClass,
      setFile,
      open,
      reset,
      imageUrl,
      previewType,
      fileName,
      hasPreview,
      progress,
      ocrStatus,
      ocrData,
      isProcessing,
      statusClass,
      processingLabel,
      fileupload,
      acceptAttr,
      acceptedHint,
      errorMessage,
    };
  },
};
</script>

<style lang="scss" scoped>
.file-upload-ocr {
  @apply rounded-lg;
  @apply border-2;
  @apply border-dashed;
  @apply border-purple-300;
  @apply w-full;
  @apply h-auto;
  @apply cursor-pointer;
  @apply flex;
  @apply justify-center;
  @apply items-center;
  @apply py-4;
  @apply px-3;
  @apply transition-all;
  @apply flex-col;
  @apply text-center;
  @apply relative;
  @apply overflow-hidden;
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  min-height: 120px;

  &:hover {
    @apply border-purple-500;
    background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);

    i.upload-main,
    i.processing-icon {
      @apply text-purple-600;
    }
  }

  &.is-processing {
    @apply cursor-wait;
    @apply border-purple-400;
    background: linear-gradient(135deg, #ede9fe 0%, #e0e7ff 100%);
  }

  &.is-done {
    @apply border-green-400;
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  }

  &.is-error {
    @apply border-red-400;
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  }

  i {
    @apply text-purple-500;
    @apply text-4xl;
    @apply transition-all;
    @apply inline-block;
    @apply mb-1;
  }

  .upload-label {
    @apply text-purple-700;
    @apply text-sm;
    @apply font-semibold;
    @apply mt-1;
  }

  .upload-hint {
    @apply text-purple-400;
    @apply text-xs;
    @apply mt-1;
    @apply italic;
  }

  .ocr-badge {
    @apply absolute;
    @apply top-2;
    @apply left-2;
    @apply text-xs;
    @apply font-bold;
    @apply px-2;
    @apply py-0.5;
    @apply rounded-full;
    @apply text-white;
    @apply flex;
    @apply items-center;
    @apply gap-1;
    background: linear-gradient(90deg, #8b5cf6 0%, #6366f1 100%);
    box-shadow: 0 2px 6px rgba(139, 92, 246, 0.35);

    i {
      @apply text-white;
      @apply text-xs;
      @apply m-0;
    }
  }

  .reset-btn {
    @apply absolute;
    @apply right-2;
    @apply top-2;
    @apply text-base;
    @apply text-purple-600;
    @apply bg-white;
    @apply rounded-full;
    @apply w-6;
    @apply h-6;
    @apply flex;
    @apply items-center;
    @apply justify-center;
    @apply shadow;
    @apply m-0;
    z-index: 2;

    &:hover {
      @apply text-red-500;
    }
  }

  img {
    @apply h-20;
    @apply rounded;
    @apply mt-2;
  }

  .pdf-preview {
    @apply flex;
    @apply flex-col;
    @apply items-center;
    @apply justify-center;
    @apply mt-2;
    @apply gap-1;
    @apply w-full;
    @apply px-4;

    &__icon {
      @apply text-red-500;
      @apply text-5xl;
      @apply m-0;
    }

    &__name {
      @apply text-purple-700;
      @apply text-xs;
      @apply font-semibold;
      @apply truncate;
      @apply max-w-full;
      @apply text-center;
    }
  }

  .processing-icon {
    @apply text-purple-600;
    animation: pulse 1.4s ease-in-out infinite;
  }

  .processing-label {
    @apply text-purple-700;
    @apply text-sm;
    @apply font-semibold;
    @apply mb-2;
  }

  .progress-track {
    @apply w-11/12;
    @apply h-2;
    @apply bg-purple-100;
    @apply rounded-full;
    @apply overflow-hidden;
    @apply mt-1;
  }

  .progress-bar {
    @apply h-full;
    @apply rounded-full;
    background: linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, #8b5cf6 100%);
    background-size: 200% 100%;
    transition: width 0.2s ease-out;
    animation: shimmer 1.5s linear infinite;
  }

  .progress-text {
    @apply text-purple-600;
    @apply text-xs;
    @apply font-bold;
    @apply mt-1;
  }

  .ocr-status {
    @apply text-xs;
    @apply font-semibold;
    @apply mt-2;
    @apply flex;
    @apply items-center;
    @apply gap-1;

    i {
      @apply text-base;
      @apply m-0;
    }

    &--ok {
      @apply text-green-600;
      i {
        @apply text-green-600;
      }
    }

    &--err {
      @apply text-red-600;
      i {
        @apply text-red-600;
      }
    }
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
